// Scene intro (2026-10-13): the briefing, read aloud and shown. The camera turns to each person as they are named,
// with a caption and a name card. It is optional ("Watch the intro" on the briefing) and always ends back on the
// written briefing, which stays the fallback: any failure, Skip, or no 3D cast simply returns to it.
// The words come from the scenario (briefing + cast); the server voices them with the install's narrator voice when
// one is set (intro.cfm), otherwise the intro plays as captions only.

const byId = id => document.getElementById(id);
const wait = ms => new Promise(resolve => setTimeout(resolve, ms));

export function introSegments(definition) {
  const b = definition.briefing || {};
  const custom = Array.isArray(b.intro) ? b.intro : [];
  if (custom.length) return custom.map(s => ({ text: String(s.text || ''), focus: String(s.focus || '').toLowerCase() })).filter(s => s.text);
  const out = [];
  if (b.role) out.push({ text: b.role, focus: '' });
  (definition.characters || []).forEach(c => {
    if (c.name) out.push({ text: `${c.name}, ${String(c.role || 'part of the scene').replaceAll('_', ' ')}.`, focus: String(c.key || '').toLowerCase(), name: c.name });
  });
  if (b.situation) out.push({ text: b.situation, focus: '' });
  if (b.goal) out.push({ text: `Your goal: ${b.goal}`, focus: '' });
  return out;
}

export class SceneIntro {
  constructor({ engine, definition, api, program, scenario, as = '' }) {
    this.as = as;
    this.engine = engine;
    this.definition = definition;
    this.api = api;
    this.program = program;
    this.scenario = scenario;
    this.playing = false;
    this.cancelled = false;
    this.audio = null;
  }

  // The intro is the first thing a player sees when the 3D cast is on screen: a start gate (the tap also unlocks
  // sound), with the written briefing one tap away. Without a 3D cast nothing changes: the written briefing shows.
  mount() {
    this.mountButton();
    if (!this.engine?.stagedMode || byId('introGate') || !introSegments(this.definition).length) return;
    const gate = document.createElement('div');
    gate.id = 'introGate';
    gate.className = 'intro-gate';
    const title = (this.definition.title || '').toString();
    gate.innerHTML = `<div class="intro-gate-card"><small>THE SCENE</small>${title ? `<h2></h2>` : ''}<button type="button" class="intro-gate-play" id="introGatePlay">&#9654; Start the intro</button><button type="button" class="intro-gate-read" id="introGateRead">Read the briefing instead</button></div>`;
    if (title) gate.querySelector('h2').textContent = title;
    document.body.appendChild(gate);
    byId('briefing').classList.remove('open');
    byId('introGatePlay').addEventListener('click', () => { gate.remove(); this.play(); });
    byId('introGateRead').addEventListener('click', () => { gate.remove(); byId('briefing').classList.add('open'); });
  }

  // "Watch the intro" on the written briefing too (watch again, or after reading)
  mountButton() {
    if (!this.engine?.stagedMode || byId('introBtn')) return;
    const card = document.querySelector('.briefing-card');
    const firstMode = card?.querySelector('[data-mode]');
    if (!card || !firstMode) return;
    const button = document.createElement('button');
    button.id = 'introBtn';
    button.type = 'button';
    button.className = 'intro-btn';
    button.innerHTML = '<strong>&#9654; Watch the intro</strong><span>Meet the people in the scene before you start</span>';
    button.addEventListener('click', () => this.play());
    // right under the cast list, so it is in view without scrolling (else just above the play modes)
    const castBlock = byId('briefCast')?.closest('section') || byId('briefCast');
    if (castBlock && castBlock.parentElement) castBlock.parentElement.insertBefore(button, castBlock.nextSibling);
    else firstMode.parentElement.parentElement.insertBefore(button, firstMode.parentElement);
  }

  overlay() {
    let el = byId('introOverlay');
    if (el) return el;
    el = document.createElement('div');
    el.id = 'introOverlay';
    el.className = 'intro-overlay';
    el.innerHTML = '<div class="intro-name" id="introName" hidden></div><p class="intro-caption" id="introCaption"></p><button type="button" class="intro-skip" id="introSkip">Skip intro</button>';
    document.body.appendChild(el);
    byId('introSkip').addEventListener('click', () => this.stop());
    return el;
  }

  async voices(segments) {
    try {
      const res = await this.api.post('intro', { program: this.program, scenario: this.scenario, as: this.as });
      const list = Array.isArray(res.segments) ? res.segments : [];
      if (list.length === segments.length) return list.map(s => String(s.audiourl || s.audioURL || ''));
    } catch { /* captions only */ }
    return segments.map(() => '');
  }

  async play() {
    if (this.playing) return;
    const segments = introSegments(this.definition);
    if (!segments.length) return;
    this.playing = true;
    this.cancelled = false;
    const opening = this.definition.nodes?.[this.definition.openingnode || this.definition.openingNode] || {};
    const el = this.overlay();
    byId('briefing').classList.remove('open');
    document.body.classList.add('intro-playing');
    el.hidden = false;
    byId('introCaption').textContent = 'Getting the scene ready...';
    try {
      const urls = await this.voices(segments);
      for (let i = 0; i < segments.length && !this.cancelled; i += 1) {
        const seg = segments[i];
        this.engine.stage({ ...opening, focus: seg.focus || '' });
        this.engine.introShot = seg.focus ? 'close' : 'wide';
        const nameEl = byId('introName');
        nameEl.hidden = !seg.name;
        nameEl.textContent = seg.name || '';
        byId('introCaption').textContent = seg.text;
        const minMs = Math.max(2600, seg.text.length * 55);
        const started = Date.now();
        if (urls[i]) await this.say(urls[i], minMs + 6000);
        const left = minMs - (Date.now() - started);
        if (left > 0 && !this.cancelled) await wait(urls[i] ? 400 : left);
      }
    } catch { /* fall through to the written briefing */ }
    this.finish(opening);
  }

  say(url, maxMs) {
    return new Promise(resolve => {
      const audio = new Audio(url);
      this.audio = audio;
      let done = false;
      const end = () => { if (!done) { done = true; resolve(); } };
      audio.addEventListener('ended', end);
      audio.addEventListener('error', end);
      setTimeout(end, maxMs);
      audio.play().catch(end);
    });
  }

  stop() {
    this.cancelled = true;
    if (this.audio) { try { this.audio.pause(); } catch { /* gone */ } }
  }

  finish(opening) {
    this.playing = false;
    if (this.audio) { try { this.audio.pause(); } catch { /* gone */ } this.audio = null; }
    const el = byId('introOverlay');
    if (el) el.hidden = true;
    document.body.classList.remove('intro-playing');
    this.engine.introShot = '';
    try { this.engine.stage(opening); } catch { /* the briefing still opens */ }
    byId('briefing').classList.add('open');
  }
}
